/**
 * The bar across the top of every screen, following the prototype's navbar
 * (`.local/prototype/builder.dc.html`): product mark and name on the left with
 * the connection switcher beside it, theme switch and account controls on the
 * right.
 *
 * The product name links home to the views list, the way into the browse and
 * builder screens from anywhere. While signed in, Settings and sign-out are
 * always within reach (FR-005); both are icons, named by a tooltip.
 *
 * @author John Grimes
 */

import { GearIcon, SignOutIcon, TableIcon } from "@primer/octicons-react";
import { Link } from "react-router";

import { ConnectionSwitcher } from "./ConnectionSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { useSignOut } from "../features/auth/useSession";

import type { JSX } from "react";

/** Props for {@link AppNavbar}. */
export type AppNavbarProps = {
  /** The signed-in account's username; absent before sign-in. */
  username?: string;
};

/** How many characters of the username the avatar shows. */
const INITIALS_LENGTH = 2;

/**
 * Renders the application navbar.
 *
 * @param props - The component's props.
 * @param props.username - The signed-in account's username.
 * @returns The navbar element.
 *
 * @example
 * ```tsx
 * <AppNavbar username="jo" />
 * ```
 */
export const AppNavbar = ({
  username,
}: Readonly<AppNavbarProps>): JSX.Element => {
  const signedIn = username !== undefined;
  const { signOut, isPending: signingOut } = useSignOut();

  return (
    <header className="navbar bg-base-100 border-b border-base-300 min-h-14 px-3 sm:px-5 flex-wrap gap-y-2">
      <div className="order-1 flex-1 flex items-center gap-3 min-w-0">
        <Link
          to="/"
          className="no-deco flex items-center gap-2 text-sm font-semibold shrink-0"
        >
          <span className="inline-grid place-items-center w-7 h-7 rounded-lg bg-primary text-primary-content">
            <TableIcon size={14} aria-hidden />
          </span>
          Viewport
        </Link>
      </div>
      {/*
        The prototype drops the server indicator below `md` because its own
        is a badge. Ours is the control that changes server, so it cannot go
        away; instead it takes a row of its own until there is room beside
        the account controls. Without this it overflows the left group at
        360 px and paints over the theme, Settings and Sign out buttons,
        leaving all three unclickable (SC-005).
      */}
      {signedIn && (
        <div className="order-3 w-full min-w-0 sm:order-2 sm:w-auto sm:mr-3">
          <ConnectionSwitcher />
        </div>
      )}
      <div className="order-2 sm:order-3 flex items-center gap-1">
        {/* Without account controls, the theme switch ends the bar. */}
        <ThemeToggle tooltipSide={signedIn ? "bottom" : "left"} />
        {signedIn && (
          <>
            <div className="tooltip tooltip-bottom" data-tip="Settings">
              <Link
                to="/settings"
                className="btn btn-ghost btn-circle"
                aria-label="Settings"
              >
                <GearIcon size={15} aria-hidden />
              </Link>
            </div>
            {/*
              Below `sm` the avatar is hidden and this is the last control in
              the bar, so a tooltip centred beneath it would run off the right
              edge at 360 px (SC-005).
            */}
            <div
              className="tooltip tooltip-left sm:tooltip-bottom"
              data-tip={signingOut ? "Signing you out…" : "Sign out"}
            >
              <button
                type="button"
                className="btn btn-ghost btn-circle"
                aria-label="Sign out"
                onClick={signOut}
                disabled={signingOut}
              >
                <SignOutIcon size={15} aria-hidden />
              </button>
            </div>
            <span className="sr-only">Signed in as {username}</span>
            <div className="avatar avatar-placeholder hidden sm:inline-flex select-none">
              <div className="w-7 rounded-full bg-neutral text-neutral-content">
                <span className="text-sm" aria-hidden>
                  {username.slice(0, INITIALS_LENGTH).toUpperCase()}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
};
